/**
 * Client Supabase (comptes + bibliothèque cloud).
 *
 * La config vient du build (.env.production) :
 *   VITE_SUPABASE_URL=https://supabase.exemple.com
 *   VITE_SUPABASE_ANON_KEY=...
 * Sans config, l'app fonctionne comme avant : les boutons de compte
 * sont simplement masqués (accountsEnabled() === false).
 */
import { useEffect, useState } from 'react';
import { createClient, type SupabaseClient, type User } from '@supabase/supabase-js';
import { track } from './track';

const env = (import.meta as unknown as { env: Record<string, string | undefined> }).env ?? {};
const URL_ = env.VITE_SUPABASE_URL;
const KEY = env.VITE_SUPABASE_ANON_KEY;

export const supabase: SupabaseClient | null =
  URL_ && KEY ? createClient(URL_, KEY, { auth: { persistSession: true } }) : null;

export function accountsEnabled(): boolean {
  return supabase != null;
}

/** Hook : utilisateur connecté (null si déconnecté ou comptes non configurés). */
export function useAccount(): User | null {
  const [user, setUser] = useState<User | null>(null);
  useEffect(() => {
    if (!supabase) return;
    supabase.auth.getSession().then(({ data }) => setUser(data.session?.user ?? null));
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });
    return () => sub.subscription.unsubscribe();
  }, []);
  return user;
}

function frMessage(raw: string): string {
  const m = raw.toLowerCase();
  if (m.includes('invalid login credentials')) return 'Email ou mot de passe incorrect.';
  if (m.includes('user already registered')) return 'Un compte existe déjà avec cet email.';
  if (m.includes('password should be at least')) return 'Mot de passe trop court (6 caractères minimum).';
  if (m.includes('unable to validate email') || m.includes('invalid email')) return 'Adresse email invalide.';
  if (m.includes('email not confirmed')) return 'Email non confirmé — vérifie ta boîte mail (ou active l’auto-confirmation côté serveur).';
  if (m.includes('rate limit')) return 'Trop de tentatives, réessaie dans une minute.';
  return `Connexion impossible : ${raw}`;
}

export async function signIn(email: string, password: string): Promise<string | null> {
  if (!supabase) return 'Comptes non configurés sur ce serveur.';
  const { error } = await supabase.auth.signInWithPassword({ email, password });
  if (!error) track('connexion-reussie');
  return error ? frMessage(error.message) : null;
}

export async function signUp(email: string, password: string): Promise<string | null> {
  if (!supabase) return 'Comptes non configurés sur ce serveur.';
  const { error } = await supabase.auth.signUp({ email, password });
  if (!error) track('inscription-reussie');
  return error ? frMessage(error.message) : null;
}

export async function signOut(): Promise<void> {
  await supabase?.auth.signOut();
}

/** uuid v4 — crypto.randomUUID avec repli pour les vieux Safari iOS. */
export function uuid(): string {
  const c = globalThis.crypto;
  if (c && 'randomUUID' in c) return c.randomUUID();
  const b = new Uint8Array(16);
  c.getRandomValues(b);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, '0'));
  return `${h[0]}${h[1]}${h[2]}${h[3]}-${h[4]}${h[5]}-${h[6]}${h[7]}-${h[8]}${h[9]}-${h[10]}${h[11]}${h[12]}${h[13]}${h[14]}${h[15]}`;
}
